import MessageBubble from "./MessageBubble";
import ChatInput from "./ChatInput";
import {
  ArrowLeft,
  Eye,
  Phone,
  Trash2,
  UsersRound,
  Video,
} from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  getChatHistoryApi,
  deleteChatGroupApi,
  getChatUnreadApi,
  getGroupMembersApi,
} from "../../app/api";
import { getSocket } from "../../socket/socket";
import { useNotificationStore } from "../../app/notificationStore";
import { useCallStore } from "../../app/callStore";
import { getChatDayLabel, getDayKey, getGroupImageUrl, getMediaUrl, parseChatDate } from "./chatUtils";

export default function ChatWindow({
  user,
  group,
  onBack,
  currentUser,
  onInspectGroup,
  onGroupDeleted
}) {
  const [messages, setMessages] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const messagesEndRef = useRef(null);
  const [socket, setSocket] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [typingUser, setTypingUser] = useState(null);
  const typingTimerRef = useRef(null);
  const outgoingTypingTimerRef = useRef(0);

  const { setCallState, activeGroupCalls, callActive } = useCallStore();
  const activeGroupCall = group ? activeGroupCalls.find(c => c.groupId === group.id) : null;
  const directUserName =
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
    user?.name ||
    user?.senderName ||
    "User";

  const getMessageId = useCallback((message) =>
    message?.messageId ??
    message?.message_id ??
    message?.id ??
    message?.message?.id ??
    message?.message?.message_id, []);

  const isSameChatMessage = useCallback((data) => {
    const payloadGroupId = data?.groupId ?? data?.group_id ?? data?.message?.groupId ?? data?.message?.group_id;
    const payloadSenderId = data?.senderId ?? data?.sender_id ?? data?.message?.senderId ?? data?.message?.sender_id;
    const payloadReceiverId = data?.receiverId ?? data?.receiver_id ?? data?.message?.receiverId ?? data?.message?.receiver_id;

    if (group) return String(payloadGroupId) === String(group.id);
    if (!user) return true;

    return (
      String(payloadSenderId) === String(user.id) ||
      String(payloadReceiverId) === String(user.id) ||
      String(data?.deletedBy) === String(user.id)
    );
  }, [group, user]);

  const markMessageDeleted = useCallback((messageId) => {
    if (!messageId) return;
    setMessages((prev) =>
      prev.map((m) =>
        String(getMessageId(m)) === String(messageId)
          ? { ...m, is_deleted: 1, isDeleted: true }
          : m
      )
    );
  }, [getMessageId]);

  const getMessageSenderId = useCallback((message) =>
    message?.senderId ??
    message?.sender_id ??
    message?.sender?.id ??
    message?.user_id, []);

  const getSenderName = useCallback((message) => {
    const directName =
      message?.senderName ||
      message?.sender_name ||
      message?.name ||
      [message?.sender_first_name, message?.sender_last_name].filter(Boolean).join(" ").trim() ||
      [message?.first_name, message?.last_name].filter(Boolean).join(" ").trim() ||
      [message?.sender?.first_name, message?.sender?.last_name].filter(Boolean).join(" ").trim() ||
      message?.sender?.name ||
      "";

    if (directName) return directName;

    const senderId = getMessageSenderId(message);
    const member = groupMembers.find((m) =>
      String(m.id || m.user_id || m.employee_id) === String(senderId)
    );

    return (
      [member?.first_name, member?.last_name].filter(Boolean).join(" ").trim() ||
      member?.name ||
      (senderId ? `User #${senderId}` : "")
    );
  }, [getMessageSenderId, groupMembers]);

  // =========================
  // 📥 FETCH HISTORY + SOCKET
  // =========================
  useEffect(() => {
    const currentSocket = getSocket();
    setSocket(currentSocket);

    const fetchHistory = async () => {
      try {
        const res = await getChatHistoryApi({
          userId: user?.id,
          groupId: group?.id,
        });

        if (res?.success) {
          const formatted = res.messages.map((m) => ({
            ...m,
            text: m.message,
            type: m.type || "text",
            sender: m.sender_id === currentUser.id ? "me" : "them",
            senderName: getSenderName(m),
          }));
          setMessages(formatted);
        }
      } catch (err) {
        console.error("Failed to fetch chat history", err);
      }
    };

    fetchHistory();
    if (group?.id) {
       getGroupMembersApi(group.id).then(res => {
         if (res?.success) setGroupMembers(res.members || []);
       }).catch(() => {});
    }

    // ================= SOCKET EVENTS =================
    const handleReceive = (data) => {
      if (group && data.groupId !== group.id) return;
      if (
        user &&
        !data.groupId &&
        data.senderId !== user.id &&
        data.receiverId !== user.id
      )
        return;

      setMessages((prev) => {
        // Prevent duplicates by checking data.id (which comes from backend)
        if (data.id && prev.some(m => m.id === data.id)) return prev;
        return [
          ...prev,
          {
            ...data,
            text: data.message,
            type: data.type || "text",
            sender: data.senderId === currentUser.id ? "me" : "them",
            senderName: getSenderName(data),
          },
        ];
      });
    };

    const handleSent = (data) => {
      if (group && data.groupId !== group.id) return;
      if (user && !data.groupId && data.receiverId !== user.id) return;

      setMessages((prev) => {
        // Prevent duplicates
        if (data.id && prev.some(m => m.id === data.id)) return prev;
        return [
          ...prev,
          { ...data, text: data.message, type: data.type || "text", sender: "me", senderName: getSenderName(data) },
        ];
      });
    };

    const handleSeen = (data) => {
      if (user && data.seenBy === user.id) {
        setMessages((prev) =>
          prev.map((m) =>
            m.sender === "me" ? { ...m, is_seen: 1 } : m
          )
        );
      }
    };

    const handleDeleted = (data) => {
      if (!isSameChatMessage(data)) return;
      markMessageDeleted(getMessageId(data));
    };

    const handleTyping = (data = {}) => {
      const senderId = data.senderId ?? data.sender_id;
      if (!user || String(senderId) !== String(user.id)) return;

      setTypingUser(directUserName);
      window.clearTimeout(typingTimerRef.current);
      typingTimerRef.current = window.setTimeout(() => setTypingUser(null), 1800);
    };

    if (currentSocket) {
      currentSocket.on("receive_message", handleReceive);
      currentSocket.on("message_sent", handleSent);
      currentSocket.on("message_seen", handleSeen);
      currentSocket.on("typing", handleTyping);
      currentSocket.on("message_deleted", handleDeleted);
      currentSocket.on("delete_message", handleDeleted);
      currentSocket.on("message_delete", handleDeleted);
    }

    return () => {
      if (currentSocket) {
        currentSocket.off("receive_message", handleReceive);
        currentSocket.off("message_sent", handleSent);
        currentSocket.off("message_seen", handleSeen);
        currentSocket.off("typing", handleTyping);
        currentSocket.off("message_deleted", handleDeleted);
        currentSocket.off("delete_message", handleDeleted);
        currentSocket.off("message_delete", handleDeleted);
      }
      window.clearTimeout(typingTimerRef.current);
    };
  }, [user?.id, group?.id, currentUser.id, group, user, getMessageId, getSenderName, isSameChatMessage, markMessageDeleted, directUserName]);

  // =========================
  // 📍 TRACK ACTIVE CHAT (for notification suppression)
  // =========================
  useEffect(() => {
    const { setActiveChat, setActiveGroup } = useNotificationStore.getState();
    if (user?.id) setActiveChat(user.id);
    if (group?.id) setActiveGroup(group.id);

    return () => {
      setActiveChat(null);
      setActiveGroup(null);
    };
  }, [user?.id, group?.id]);

  useEffect(() => {
    if (!group?.id || groupMembers.length === 0) return;

    setMessages((prev) =>
      prev.map((message) =>
        message.sender === "them" && !message.senderName
          ? { ...message, senderName: getSenderName(message) }
          : message
      )
    );
  }, [group?.id, groupMembers, getSenderName]);

  // =========================
  // 👁 MARK AS SEEN
  // =========================
  useEffect(() => {
    if (!socket) return;

    if (user) {
      const hasUnseen = messages.some(
        (m) => m.sender === "them" && !m.is_seen
      );

      if (hasUnseen) {
        socket.emit("mark_seen", { senderId: user.id });

        getChatUnreadApi()
          .then((res) => {
            if (res?.success) {
              useNotificationStore
                .getState()
                .setChatUnreadCount(res.count);
            }
          })
          .catch(() => {});
      }
    } else if (group) {
      socket.emit("mark_group_seen", { groupId: group.id });
    }
  }, [messages, socket, user, group]);

  // =========================
  // 🔽 AUTO SCROLL
  // =========================
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // =========================
  // 📤 SEND MESSAGE
  // =========================
  const handleSendMessage = (text, type = "text") => {
    if (!text || !socket) return;

    const payload = {
      message: text,
      type: type,
      clientTime: new Date(),
    };

    if (group) payload.groupId = group.id;
    if (user) payload.receiverId = user.id;

    socket.emit("send_message", payload);
  };

  const handleTyping = useCallback(() => {
    if (!socket || !user?.id || group?.id) return;

    const now = Date.now();
    if (now - outgoingTypingTimerRef.current < 1200) return;

    outgoingTypingTimerRef.current = now;
    socket.emit("typing", { receiverId: user.id });
  }, [socket, user?.id, group?.id]);

  // =========================
  // ❌ DELETE GROUP
  // =========================
  const handleDeleteGroup = async () => {
    try {
      const res = await deleteChatGroupApi(group.id);

      if (res?.success) {
        setShowDeleteModal(false);
        onGroupDeleted();
      }
    } catch (err) {
      alert("Failed to delete group");
    }
  };

  // =========================
  // 📞 INITIATE CALL
  // =========================
  const initiateCall = (video) => {
    if (!socket) return;
    
    const targetId = user?.id || null;
    const groupId = group?.id || null;

    setCallState({
      isCalling: true,
      targetUserId: targetId,
      targetUserName: targetId ? `${user.first_name} ${user.last_name}` : group.name,
      groupId: groupId,
      isVideo: video,
    });
  };

  const isAdmin = [1, 2, 3].includes(currentUser?.role_id);
  const canManage =
    group && (isAdmin || group.created_by === currentUser?.id);
  const title = group
    ? group.name
    : `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  const avatarLetter = group
    ? group.name?.charAt(0).toUpperCase()
    : user?.first_name?.charAt(0);
  const groupImageUrl = group ? getGroupImageUrl(group) : "";
  const directImageUrl = !group ? getMediaUrl(user?.profile_pic || user?.profilePic || user?.senderProfilePic || "") : "";
  const messageGroups = messages.reduce((groups, message) => {
    const date = parseChatDate(message.created_at || message.createdAt);
    const dayKey = getDayKey(date);
    const latestGroup = groups[groups.length - 1];

    if (latestGroup?.dayKey === dayKey) {
      latestGroup.messages.push(message);
    } else {
      groups.push({
        dayKey,
        label: getChatDayLabel(date),
        messages: [message],
      });
    }

    return groups;
  }, []);

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-[#171421]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-violet-950/35 to-transparent" />

      {/* HEADER */}
      <div className="relative border-b border-white/10 bg-[#171421]/95 px-3 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <button
            onClick={onBack}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/75 transition hover:bg-white/10 hover:text-white"
            title="Back"
          >
            <ArrowLeft size={17} />
          </button>

          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-violet-600 font-bold text-white ring-1 ring-white/15">
            {group && groupImageUrl ? (
              <img
                src={groupImageUrl}
                alt={title || "Group"}
                className="h-full w-full object-cover"
              />
            ) : directImageUrl ? (
              <img
                src={directImageUrl}
                alt={title || "User"}
                className="h-full w-full object-cover"
              />
            ) : group ? (
              <UsersRound size={17} />
            ) : (
              avatarLetter
            )}
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate text-[13px] font-semibold text-white sm:text-sm">{title}</p>
              {group && (
                <button
                  onClick={onInspectGroup}
                  className="rounded-full p-1 text-white/45 transition hover:bg-white/10 hover:text-white"
                  title="View members"
                >
                  <Eye size={14} />
                </button>
              )}
            </div>
            <p className="mt-0.5 truncate text-[11px] text-white/40">
              {typingUser ? (
                <span className="text-cyan-200">{typingUser} is typing...</span>
              ) : group ? `${groupMembers.length || "Team"} members` : "Direct message"}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {canManage && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex h-8 w-8 items-center justify-center rounded-full text-rose-300 transition hover:bg-rose-500/15"
              title="Delete group"
            >
              <Trash2 size={15} />
            </button>
          )}

          <button 
            onClick={() => initiateCall(false)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition hover:bg-emerald-500/15 hover:text-emerald-200"
            title="Audio call"
          >
            <Phone size={15} />
          </button>

          <button
            onClick={() => initiateCall(true)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition hover:bg-sky-500/15 hover:text-sky-200"
            title="Video call"
          >
            <Video size={15} />
          </button>
        </div>
        </div>
      </div>
      
      {/* JOIN CALL BANNER */}
      {activeGroupCall && !callActive && (
        <div className="relative flex items-center justify-between border-b border-emerald-300/20 bg-emerald-500/10 px-5 py-3 animate-in slide-in-from-top duration-300">
           <div className="flex items-center gap-3">
              <div className="w-2.5 h-2.5 bg-[#00a884] rounded-full animate-pulse shadow-[0_0_8px_rgba(0,168,132,0.6)]"></div>
              <div className="flex flex-col">
                <p className="text-xs font-bold text-[#00a884] uppercase tracking-wider">Group Call Active</p>
                <p className="text-[10px] text-white/50">{activeGroupCall.senderName} started a call</p>
              </div>
           </div>
           <button 
             onClick={() => initiateCall(activeGroupCall.isVideo)}
             className="bg-[#00a884] text-white px-5 py-1.5 rounded-lg text-xs font-black hover:bg-[#008f6f] transition-all shadow-lg active:scale-95"
           >
             JOIN CALL
           </button>
        </div>
      )}

      {/* MESSAGES */}
      <div className="hide-scrollbar relative flex-1 overflow-y-auto px-3 py-5">
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-slate-900/35 to-transparent" />
        <div className="relative space-y-3">
        {messageGroups.map((dayGroup) => (
          <div key={dayGroup.dayKey} className="space-y-3">
            <div className="sticky top-0 z-10 flex justify-center py-2">
              <span className="rounded-full border border-white/10 bg-[#242032]/95 px-4 py-1.5 text-[10px] font-bold tracking-wide text-white/65 shadow-lg shadow-black/15 backdrop-blur">
                {dayGroup.label}
              </span>
            </div>

            {dayGroup.messages.map((msg, index) => (
              <div key={getMessageId(msg) || `${dayGroup.dayKey}-${index}`}>
                {msg.sender === "them" && (
                  <span className="mb-1 ml-3 block text-[10px] font-bold uppercase tracking-wide text-white/35">
                    {group
                      ? msg.senderName || getSenderName(msg)
                      : msg.senderName || directUserName}
                  </span>
                )}
                <MessageBubble
                  message={msg}
                  onDelete={(id) => {
                    markMessageDeleted(id);
                    socket?.emit("delete_message", {
                      messageId: id,
                      id,
                      groupId: group?.id,
                      receiverId: user?.id,
                      deletedBy: currentUser?.id,
                    });
                  }}
                />
              </div>
            ))}
          </div>
        ))}
        {typingUser && (
          <div className="ml-3 flex items-center gap-1.5 text-[11px] font-semibold text-white/45">
            <span>{typingUser} is typing</span>
            <span className="flex gap-1">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cyan-200/70 [animation-delay:-0.2s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cyan-200/70 [animation-delay:-0.1s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-cyan-200/70" />
            </span>
          </div>
        )}
        <div ref={messagesEndRef} />
        </div>
      </div>

      {/* INPUT */}
      <ChatInput onSend={handleSendMessage} onTyping={handleTyping} members={group?.id ? groupMembers : []} />

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/70 px-4 backdrop-blur-md">
          <div className="w-[320px] rounded-3xl border border-white/10 bg-[#171421] p-6 text-center shadow-2xl">
            <h4 className="text-lg font-bold mb-2">
              Delete Group
            </h4>
            <p className="mb-6 text-sm text-white/55">
              Are you sure you want to delete this group?
            </p>

            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="rounded-xl border border-white/10 bg-white/10 px-4 py-2 text-sm text-white/70 transition hover:bg-white/15"
              >
                Cancel
              </button>

              <button
                onClick={handleDeleteGroup}
                className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 💅 STYLES TO HIDE SCROLLBARS */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none !important;
        }
        .hide-scrollbar {
          -ms-overflow-style: none !important;
          scrollbar-width: none !important;
        }
      `}</style>
    </div>
  );
}
